// The in-memory API's process engine (Parity P5; services/api/src/routes/automations.ts and
// routes/cadence/recurring-controls.ts), with the real routes' rules: a board's rule is written
// by whoever edits the board (here: the board's company members who are not client viewers), a
// company-wide rule by an owner or admin; every write but a move in the list makes the writer the
// owner; If-Match on PATCH; a duplicate is switched off; switching on clears a pause. Recurring
// controls are managers': pause (409 when paused), resume (the next occurrence after now; "after
// completion" waits for an open last task), skip (the schedule moves past the next occurrence),
// take over (the open occurrence's task, the control's last one: 409 without; or all future
// occurrences, the routine's own seat handing them back), Run now (a real task in the work twin)
// and the history. Schedules go through the API's own parser and generator
// (`@companyos/domain-work/recurrence`), in the company's time zone.
// Rules never fire here (no relay): History shows the demo firings only.
//
// Demo rows (this twin only; the server seeds none): Acme's first board has three rules (one
// paused because its owner lost access) and one agent proposal; its first responsibility has a
// weekly routine, and a second, paused one.
import { formatRule, nextAfter, parseRule } from "@companyos/domain-work/recurrence";
import type { OperationInput, OperationOutput, RouteId } from "@companyos/sdk";
import { type Handler, json, notFound, type Principal, problem, validation } from "./fake";
import { COMPANY_IDS, uid } from "./fake-data";

type Rule = OperationOutput<"automations.get">;
type Spec = OperationInput<"automations.create">["body"];
type Proposal = OperationOutput<"automations.list">["proposals"][number];
type Run = OperationOutput<"automations.runs">["data"][number];
type Routine = OperationOutput<"seats.routines">["data"][number];
type RecurringRun = OperationOutput<"recurringWork.history">["data"][number];
type RecurringWork = Routine["recurring_work"];
type Control = NonNullable<Routine["control"]>;
type Responsibility = OperationOutput<"responsibilities.create">;
type Trigger = Spec["trigger"];
type Condition = Spec["conditions"][number];
type Action = Spec["actions"][number];
type TaskTemplate = RecurringWork["task_template"];
type BoardNote = OperationOutput<"boards.note">;

type BoardLite = { id: string; name: string; status_set: { key: string; name: string }[] };
type SeatLite = { id: string; title: string; status: string };

export type ProcessDeps = {
  now: () => Date;
  boardsOf: (companyId: string) => BoardLite[];
  seatsOf: (companyId: string) => SeatLite[];
  /** The org twin's responsibilities (a new one is pushed onto this list). */
  responsibilitiesOf: (companyId: string) => Responsibility[];
  /** The org twin's recurring work rows: the routines are these, the controls act on them. */
  recurringOf: (companyId: string) => RecurringWork[];
  /** The work twin's live task (an occurrence taken over is reassigned in place). */
  taskOf: (
    companyId: string,
    id: string,
  ) => {
    id: string;
    title: string;
    board_id: string;
    priority: number;
    completed_at: string | null;
    assignee_seat_ids: string[];
  } | null;
  /** A member's display name (the owner column). */
  nameOf: (companyId: string, membershipId: string) => string | null;
  /** The company calendar's time zone (schedules are read in it, as the API does). */
  timezoneOf: (companyId: string) => string;
  /** Creates a task through the work twin's `tasks.create` as `p`; its id, or null if refused. */
  createTask: (
    p: Principal,
    body: {
      board_id: string;
      title: string;
      priority?: number;
      assignee_seat_ids?: string[];
      checklist?: string[];
      due_at?: string;
    },
  ) => string | null;
};

const PRIORITY = ["none", "urgent", "high", "normal", "low"];
/** A new rule's position: its creation time in ms, as the API's column default (past the last). */
const nextPosition = (list: readonly { position: number }[]) =>
  Math.max(Date.now(), ...list.map((x) => x.position + 1000));
const forbidden = (detail?: string) => problem(403, "forbidden", "Forbidden", detail);
const conflict = (detail: string) => problem(409, "conflict", "Conflict", detail);

const membershipOf = (p: Principal) =>
  p.me.memberships.find((m) => m.company_id === p.company.id)?.membership_id ?? null;
const clientViewerOnly = (p: Principal) =>
  p.roles.length > 0 && p.roles.every((r) => r === "client_viewer");

export function createProcessHandlers(d: ProcessDeps): Partial<Record<RouteId, Handler>> {
  let seq = 0;
  const id = (group: number) => uid(group, ++seq);
  const at = () => d.now().toISOString();
  const rules = new Map<string, Rule[]>();
  const runs = new Map<string, Run[]>();
  const proposals = new Map<string, Proposal[]>();
  const controls = new Map<string, Control>();
  /** "After completion": the last task's completion as last applied (`viewOf`), per routine. */
  const reconciled = new Map<string, string | null>();
  const history = new Map<string, RecurringRun[]>();
  const seeded = new Set<string>();

  // Names and sentences --------------------------------------------------------------------------

  const boardOf = (co: string, b: string | null) =>
    b ? (d.boardsOf(co).find((x) => x.id === b) ?? null) : null;
  const seatName = (co: string, s: string) =>
    d.seatsOf(co).find((x) => x.id === s)?.title ?? "a seat";
  const statusName = (co: string, b: string | null, key: string) =>
    boardOf(co, b)?.status_set.find((s) => s.key === key)?.name ?? key;

  function triggerText(co: string, t: Trigger, b: string | null): string {
    switch (t.type) {
      case "task.created":
        return "a task is created";
      case "task.moved":
        return "a task is moved here";
      case "task.status_changed":
        return t.to ? `status becomes ${statusName(co, b, t.to)}` : "status changes";
      case "task.completed":
        return "a task is completed";
      case "task.field_changed":
        return "a field changes";
      case "task.tagged":
        return "a task is tagged";
      case "task.commented":
        return "someone comments";
      case "task.due_soon":
        return `a task is due within ${t.hours} h`;
      case "task.overdue":
        return "a task is overdue";
      case "schedule":
        return `every ${t.weekdays?.length === 5 ? "weekday" : "day"} at ${t.at}`;
      case "message.posted":
        return "a message is posted";
      case "observation.recorded":
        return "a number is recorded";
      case "date_field_reached":
        return t.offset_hours === 0
          ? "the date in a date field is reached"
          : `it is ${Math.abs(t.offset_hours)} h ${t.offset_hours < 0 ? "before" : "after"} the date in a date field`;
      case "task.file_changed":
        return t.op ? `a file is ${t.op.replace("_", " ")}` : "a task's files change";
      case "task.linked":
        return "a task is linked";
    }
  }
  function conditionText(co: string, c: Condition, b: string | null): string {
    switch (c.type) {
      case "status_is":
        return `status is ${c.statuses.map((s) => statusName(co, b, s)).join(" or ")}`;
      case "priority_is":
        return `priority is ${c.priorities.map((x) => PRIORITY[x]).join(" or ")}`;
      case "has_tag":
        return "it has the tag";
      case "title_contains":
        return `the title contains “${c.text}”`;
      case "field_equals":
        return "a field matches";
      case "metric_above":
        return `the value is above ${c.value}`;
      case "metric_below":
        return `the value is below ${c.value}`;
    }
  }
  function actionText(co: string, x: Action, b: string | null): string {
    switch (x.type) {
      case "set_status":
        return `set status ${statusName(co, b, x.status)}`;
      case "set_priority":
        return `set priority ${PRIORITY[x.priority]}`;
      case "add_tag":
        return "add the tag";
      case "add_comment":
        return `comment “${x.text}”`;
      case "assign":
        return `assign ${seatName(co, x.seat_id)}`;
      case "wake_agent":
        return `wake ${seatName(co, x.seat_id)}`;
      case "create_task":
        return `create task “${x.title}”`;
      case "move_to_board":
        return `move to ${boardOf(co, x.board_id)?.name ?? "another board"}`;
      case "wake_owner":
        return `wake the owner to “${x.brief}”`;
    }
  }
  const sentence = (
    co: string,
    s: Pick<Spec, "trigger" | "conditions" | "actions" | "board_id">,
  ) => {
    const when = `When ${triggerText(co, s.trigger, s.board_id)}`;
    const cond = s.conditions.length
      ? `, if ${s.conditions.map((c) => conditionText(co, c, s.board_id)).join(" and ")}`
      : "";
    return `${when}${cond}, then ${s.actions.map((x) => actionText(co, x, s.board_id)).join(", then ")}`;
  };

  // Seeds ----------------------------------------------------------------------------------------

  function seedRules(co: string): Rule[] {
    const board = co === COMPANY_IDS.acme ? d.boardsOf(co)[0] : undefined;
    if (!board) return [];
    const done = board.status_set.at(-1)?.key ?? "done";
    const t0 = d.now().getTime();
    const ago = (h: number) => new Date(t0 - h * 3_600_000).toISOString();
    const owner = "0193a0a0-1000-7000-8000-000000000001";
    const mk = (
      n: number,
      name: string,
      s: Pick<Spec, "trigger" | "actions">,
      more: Partial<Rule> = {},
    ) =>
      view(co, {
        company_id: co,
        id: uid(0xa500, n),
        scope: "board",
        board_id: board.id,
        name,
        trigger: s.trigger,
        conditions: [],
        actions: s.actions,
        enabled: true,
        owner_membership_id: owner,
        source: "person",
        approval_id: null,
        proposed_by_seat_id: null,
        effect_budget_per_hour: 200,
        paused_reason: null,
        paused_at: null,
        // the API's positions: a rule's creation time in ms (spread out, so a move takes a midpoint)
        position: t0 - 24 * 9 * 3_600_000 + n * 1000,
        version: 1,
        fired_count: 0,
        last_fired_at: null,
        actor_kind: "person",
        actor_membership_id: owner,
        created_at: ago(24 * 9),
        updated_at: ago(24 * 9),
        ...more,
      });
    const out = [
      mk(
        1,
        "Triage new tasks",
        { trigger: { type: "task.created" }, actions: [{ type: "set_priority", priority: 3 }] },
        { fired_count: 14, last_fired_at: ago(2) },
      ),
      mk(
        2,
        "Say well done",
        {
          trigger: { type: "task.status_changed", to: done },
          actions: [{ type: "add_comment", text: "Well done, closing the loop." }],
        },
        { fired_count: 6, last_fired_at: ago(20) },
      ),
      mk(
        3,
        "Escalate overdue",
        { trigger: { type: "task.overdue" }, actions: [{ type: "set_priority", priority: 1 }] },
        {
          paused_reason: "owner_lost_access",
          paused_at: ago(30),
          fired_count: 3,
          last_fired_at: ago(50),
        },
      ),
    ];
    runs.set(
      out[0]?.id ?? "",
      [2, 5, 26].map((h, i) => ({
        company_id: co,
        id: uid(0xa510, i + 1),
        automation_id: out[0]?.id ?? "",
        idempotency_key: uid(0xa511, i + 1),
        event_id: uid(0xa512, i + 1),
        event_type: "task.created",
        subject_kind: "task",
        subject_id: null,
        depth: 0,
        outcome: i === 2 ? ("partial" as const) : ("fired" as const),
        refused_reason: null,
        effects:
          i === 2
            ? [
                {
                  action: "set_priority",
                  ok: false,
                  code: "forbidden",
                  detail: "you do not edit this board",
                },
              ]
            : [{ action: "set_priority", ok: true }],
        effect_count: i === 2 ? 0 : 1,
        acted_as_membership_id: owner,
        acted_as_name: d.nameOf(co, owner),
        acted_as_de: false,
        created_at: ago(h),
      })),
    );
    proposals.set(co, [
      {
        approval_id: uid(0xa520, 1),
        op: "create",
        automation_id: null,
        board_id: board.id,
        requested_by_seat_id: null,
        requested_by_name: "Sourcer (agent)",
        sentence: sentence(co, {
          board_id: board.id,
          trigger: { type: "task.due_soon", hours: 24 },
          conditions: [],
          actions: [{ type: "add_comment", text: "Due tomorrow: anything blocking?" }],
        }),
        created_at: ago(3),
      },
    ]);
    return out;
  }

  const rulesOf = (co: string) => {
    let l = rules.get(co);
    if (!l) {
      l = seedRules(co);
      rules.set(co, l);
    }
    return l;
  };

  function view(
    co: string,
    r: Omit<
      Rule,
      | "owner_name"
      | "board_name"
      | "proposed_by_name"
      | "sentence"
      | "can_edit"
      | "owner_seat_id"
      | "owner_de_name"
    > &
      Partial<Rule>,
  ): Rule {
    return {
      ...r,
      owner_seat_id: r.owner_seat_id ?? null,
      owner_de_name: r.owner_de_name ?? null,
      owner_name: d.nameOf(co, r.owner_membership_id),
      board_name: boardOf(co, r.board_id)?.name ?? null,
      proposed_by_name: r.proposed_by_name ?? null,
      sentence: sentence(co, r),
      can_edit: r.can_edit ?? true,
    } as Rule;
  }

  /** Whether `p` may write `scope`/`board` (company-wide: managers; a board: a member). */
  const writes = (p: Principal, scope: string, board: string | null) =>
    scope === "company" ? p.manager : !clientViewerOnly(p) && board !== null;

  function checkSpec(p: Principal, s: Spec): Response | null {
    const co = p.company.id;
    if ((s.scope === "board") !== (s.board_id !== null))
      return validation("board_id: a board's rule names its board; a company-wide rule names none");
    if (!writes(p, s.scope, s.board_id))
      return forbidden(
        s.scope === "company"
          ? "company-wide automations are set up by an owner or admin"
          : undefined,
      );
    if (s.board_id && !boardOf(co, s.board_id)) return notFound();
    if (!s.actions.length) return validation("actions: add at least one action");
    if (s.board_id)
      for (const x of s.actions)
        if (
          x.type === "set_status" &&
          !boardOf(co, s.board_id)?.status_set.some((t) => t.key === x.status)
        )
          return validation(`actions: no status ${x.status} on this board`);
    return null;
  }

  const canSee = (p: Principal, r: Rule) => !clientViewerOnly(p) || r.scope === "company";
  const ruleIn = (p: Principal, rid: string | undefined) => {
    const r = rulesOf(p.company.id).find((x) => x.id === rid);
    return r && canSee(p, r) ? r : null;
  };
  const result = (automation: Rule | null) => ({ automation, proposed: false, approval_id: null });

  function patch(
    p: Principal,
    r: Rule,
    b: { [K in keyof Spec]?: Spec[K] | undefined } & { position?: number | undefined },
  ) {
    const fields = Object.keys(b).filter(
      (k) =>
        JSON.stringify((b as Record<string, unknown>)[k]) !==
        JSON.stringify((r as Record<string, unknown>)[k]),
    );
    const enabling = b.enabled === true && r.paused_reason !== null;
    if (!fields.length && !enabling) return r;
    Object.assign(r, b, {
      ...(enabling ? { paused_reason: null, paused_at: null } : {}),
      // a move in the list keeps the owner
      owner_membership_id:
        fields.length === 1 && fields[0] === "position" && !enabling
          ? r.owner_membership_id
          : (membershipOf(p) ?? r.owner_membership_id),
      version: r.version + 1,
      updated_at: at(),
    });
    return Object.assign(r, view(p.company.id, r));
  }

  // Routines -------------------------------------------------------------------------------------

  /** Acme's demo routines (once): a weekly one and a paused monthly one on its first responsibility. */
  function seedRoutines(co: string) {
    if (seeded.has(co)) return;
    seeded.add(co);
    if (co !== COMPANY_IDS.acme) return;
    const r = d.responsibilitiesOf(co)[0];
    if (!r) return;
    const t0 = d.now().getTime();
    const weekly = recurring(
      co,
      r.id,
      "Weekly pipeline review",
      "FREQ=WEEKLY;BYDAY=MO;BYHOUR=9",
      new Date(t0 + 2 * 86_400_000),
    );
    const monthly = recurring(
      co,
      r.id,
      "Monthly forecast check",
      "FREQ=MONTHLY;BYMONTHDAY=1;BYHOUR=9",
      null,
    );
    monthly.status = "paused";
    controls.set(
      monthly.id,
      control(co, monthly.id, { paused_at: new Date(t0 - 86_400_000).toISOString() }),
    );
    d.recurringOf(co).push(weekly, monthly);
  }

  function control(co: string, rw: string, more: Partial<Control> = {}): Control {
    return {
      company_id: co,
      recurring_work_id: rw,
      paused_at: null,
      taken_over_by_seat_id: null,
      takeover_scope: null,
      after_completion_days: null,
      last_task_id: null,
      actor_kind: "person" as const,
      actor_membership_id: null,
      created_at: at(),
      updated_at: at(),
      ...more,
    };
  }

  function recurring(
    co: string,
    responsibilityId: string,
    title: string,
    schedule: string,
    next: Date | null,
    boardId: string | null = d.boardsOf(co)[0]?.id ?? null,
  ): RecurringWork {
    return {
      company_id: co,
      id: id(0xa531),
      responsibility_id: responsibilityId,
      schedule,
      board_id: boardId,
      task_template: { title },
      status: "active",
      next_run_at: next?.toISOString() ?? null,
      last_materialised_at: null,
      actor_kind: "person",
      actor_membership_id: null,
      created_at: at(),
      updated_at: at(),
    };
  }

  /** A recurring work row as Chart › seat shows it (null: its responsibility is gone). */
  function viewOf(co: string, w: RecurringWork): Routine | null {
    const r = d.responsibilitiesOf(co).find((x) => x.id === w.responsibility_id);
    if (!r) return null;
    const c = controls.get(w.id) ?? null;
    // "after completion" follows its last task as it is now (the API's history consumer, read
    // lazily here): done, the next run is N days after; open again, none
    // only when the task's completion changed since last seen (a Skip or pause since stays)
    if (c?.after_completion_days && c.last_task_id && w.status === "active") {
      const t = d.taskOf(co, c.last_task_id);
      const state = t ? `${t.id}:${t.completed_at ?? "open"}` : null;
      if (t && reconciled.get(w.id) !== state) {
        reconciled.set(w.id, state);
        const next = t.completed_at
          ? new Date(Date.parse(t.completed_at) + c.after_completion_days * DAY).toISOString()
          : null;
        if (next !== w.next_run_at) w.next_run_at = next;
      }
    }
    return {
      recurring_work: w,
      responsibility: { id: r.id, name: r.name, seat_id: r.seat_id },
      control: c,
      board_name: boardOf(co, w.board_id)?.name ?? null,
      taken_over_by_name: c?.taken_over_by_seat_id
        ? (d.seatsOf(co).find((s) => s.id === c.taken_over_by_seat_id)?.title ?? null)
        : null,
      last_run: history.get(w.id)?.[0] ?? null,
    };
  }
  const routinesOf = (co: string): Routine[] => {
    seedRoutines(co);
    return d.recurringOf(co).flatMap((w) => viewOf(co, w) ?? []);
  };
  const routineIn = (p: Principal, rid: string | undefined) =>
    routinesOf(p.company.id).find((x) => x.recurring_work.id === rid) ?? null;
  const setControl = (r: Routine, patch: Partial<Control>) => {
    const c = {
      ...(r.control ?? control(r.recurring_work.company_id, r.recurring_work.id)),
      ...patch,
      updated_at: at(),
    };
    controls.set(r.recurring_work.id, c);
  };
  const answer = (r: Routine) => json(viewOf(r.recurring_work.company_id, r.recurring_work));
  function record(
    p: Principal,
    r: Routine,
    outcome: RecurringRun["outcome"],
    more: Partial<RecurringRun> = {},
  ) {
    const row: RecurringRun = {
      company_id: p.company.id,
      id: id(0xa532),
      recurring_work_id: r.recurring_work.id,
      outcome,
      scheduled_for: null,
      period_key: null,
      task_id: null,
      seat_id: null,
      trigger: "control",
      actor_kind: "person",
      actor_membership_id: membershipOf(p),
      created_at: at(),
      ...more,
    };
    const l = history.get(r.recurring_work.id) ?? [];
    l.unshift(row);
    history.set(r.recurring_work.id, l);
  }
  const DAY = 86_400_000;
  /**
   * The first occurrence of the row's schedule after `after`, exactly as the API's `nextRun`:
   * the shared parser and generator (`@companyos/domain-work/recurrence`), anchored at the row's
   * creation, in the company's time zone.
   */
  const nextOf = (w: RecurringWork, after: Date): Date | null => {
    const parsed = parseRule(w.schedule);
    if (!parsed.ok) return null;
    return nextAfter(parsed.rule, new Date(w.created_at), d.timezoneOf(w.company_id), after);
  };
  /**
   * "After completion": N days after the last occurrence's task was done (now once that has
   * passed); none while it is open; now without one.
   */
  function afterCompletionNext(c: Control): Date | null {
    const now = d.now();
    if (!c.last_task_id) return now;
    const t = d.taskOf(c.company_id, c.last_task_id);
    if (!t) return now;
    if (!t.completed_at) return null;
    const at = new Date(Date.parse(t.completed_at) + (c.after_completion_days ?? 0) * DAY);
    return at > now ? at : now;
  }

  function create(
    p: Principal,
    seatId: string,
    b: {
      responsibility_id?: string;
      responsibility_name?: string;
      schedule?: string;
      after_completion_days?: number;
      board_id?: string;
      task_template?: TaskTemplate;
    },
    boardId: string | null,
    /** The occurrences' task as sent (Add routine), or copied from the task (Repeat…). */
    template: TaskTemplate,
    /** "After completion" from this task (Repeat…): open, the series waits; done, N days after. */
    lastTaskId: string | null = null,
  ): Routine | Response {
    const co = p.company.id;
    const seat = d.seatsOf(co).find((s) => s.id === seatId);
    if (!seat) return notFound();
    if (seat.status === "closed") return problem(409, "seat_closed", "Seat is closed");
    if ((b.responsibility_id === undefined) === (b.responsibility_name === undefined))
      return validation("exactly one of responsibility_id and responsibility_name");
    if ((b.schedule === undefined) === (b.after_completion_days === undefined))
      return validation("exactly one of schedule and after_completion_days");
    const parsed = b.schedule !== undefined ? parseRule(b.schedule) : null;
    if (parsed && !parsed.ok)
      return problem(422, "schedule_invalid", "Schedule invalid", parsed.reason);
    if (boardId && !boardOf(co, boardId))
      return validation("board_id: no such board in this company");
    const list = d.responsibilitiesOf(co);
    let r = b.responsibility_id
      ? list.find((x) => x.id === b.responsibility_id && x.seat_id === seatId)
      : undefined;
    if (b.responsibility_id && !r)
      return validation("responsibility_id: not a responsibility of this seat");
    if (!r) {
      r = {
        company_id: co,
        id: id(0xa533),
        seat_id: seatId,
        name: (b.responsibility_name ?? "").trim(),
        description: null,
        weight: "1.000",
        kpi_metric_ids: [],
        status: "active",
        actor_kind: "person",
        actor_membership_id: membershipOf(p),
        created_at: at(),
        updated_at: at(),
      } as Responsibility;
      list.push(r);
    }
    const schedule = parsed?.ok
      ? formatRule(parsed.rule)
      : `FREQ=DAILY;INTERVAL=${b.after_completion_days}`;
    const w = recurring(co, r.id, template.title, schedule, null, boardId);
    w.task_template = template;
    if (b.after_completion_days) {
      const c = control(co, w.id, {
        after_completion_days: b.after_completion_days,
        last_task_id: lastTaskId,
      });
      controls.set(w.id, c);
      w.next_run_at = afterCompletionNext(c)?.toISOString() ?? null;
    } else {
      const next = nextOf(w, d.now());
      if (!next) return problem(422, "schedule_invalid", "Schedule invalid", "it never occurs");
      w.next_run_at = next.toISOString();
    }
    seedRoutines(co);
    d.recurringOf(co).push(w);
    return viewOf(co, w) as Routine;
  }

  const manager = (p: Principal | null) =>
    !p ? notFound() : p.manager ? null : forbidden("managers only");

  // T6-BOARD-NOTE: a board's "How this board works" (the note is kept here, per board; the
  // owner and the check window are the work twin's board row; the rules are this twin's).
  const notes = new Map<string, { version: number; md: string; updated_at: string }>();
  function noteOf(p: Principal, boardId: string): Response | BoardNote {
    const co = p.company.id;
    const b = boardOf(co, boardId) as
      | (BoardLite & {
          lead_seat_id?: string | null;
          settings?: { wake_window_minutes?: number };
          archived_at?: string | null;
        })
      | null;
    if (!b) return notFound();
    const n = notes.get(b.id);
    const lead = b.lead_seat_id ?? null;
    const seat = lead ? d.seatsOf(co).find((s) => s.id === lead) : undefined;
    const holders =
      (seat as { holders?: { membership_kind: string; display_name: string }[] } | undefined)
        ?.holders ?? [];
    const de = holders.find((h) => h.membership_kind === "digital_employee");
    const person = holders.find((h) => h.membership_kind === "person");
    const list = rulesOf(co)
      .filter((r) => r.board_id === b.id && canSee(p, r))
      .sort((x, y) => x.position - y.position);
    return {
      board_id: b.id,
      board_name: b.name,
      page_id: n ? b.id : null,
      version: n?.version ?? 0,
      body_markdown: n?.md ?? "",
      updated_at: n?.updated_at ?? null,
      owner: seat
        ? {
            seat_id: seat.id,
            title: seat.title,
            holder: de ? "de" : person ? "person" : "vacant",
            name: (de ?? person)?.display_name ?? null,
          }
        : null,
      wake_window_minutes: b.settings?.wake_window_minutes ?? 15,
      rules: list.map((r) => ({
        id: r.id,
        name: r.name,
        sentence: r.sentence,
        enabled: r.enabled,
        paused_reason: r.paused_reason,
        fired_count: r.fired_count,
        last_fired_at: r.last_fired_at,
        can_edit: writes(p, r.scope, r.board_id),
        version: r.version,
        owner_de_name: r.owner_de_name,
      })),
      rules_paused:
        list.some((r) => r.paused_reason === "board_paused") &&
        list.every((r) => !r.enabled || r.paused_reason !== null),
      can_edit: !clientViewerOnly(p) && !b.archived_at,
    };
  }
  const noteJson = (v: Response | BoardNote) => (v instanceof Response ? v : json(v));

  // Handlers -------------------------------------------------------------------------------------

  return {
    "boards.note": ({ principal: p, params }) =>
      p ? noteJson(noteOf(p, params.id ?? "")) : notFound(),
    "boards.writeNote": ({ principal: p, params, body }) => {
      if (!p) return notFound();
      const v = noteOf(p, params.id ?? "");
      if (v instanceof Response) return v;
      if (!v.can_edit) return forbidden("you do not edit this board");
      const b = (body ?? {}) as { body_markdown?: string; version?: number };
      if (b.version !== v.version)
        return problem(409, "conflict", "Conflict", `the note's version is ${v.version}`);
      const md = b.body_markdown ?? "";
      if (md !== v.body_markdown && (v.version > 0 || md.trim() !== ""))
        notes.set(v.board_id, { version: v.version + 1, md, updated_at: at() });
      return noteJson(noteOf(p, v.board_id));
    },
    "boards.pauseRules": ({ principal: p, params, body }) => {
      if (!p) return notFound();
      const v = noteOf(p, params.id ?? "");
      if (v instanceof Response) return v;
      if (!v.can_edit) return forbidden("you do not edit this board");
      const paused = (body as { paused?: boolean } | null)?.paused === true;
      for (const r of rulesOf(p.company.id)) {
        if (r.board_id !== v.board_id) continue;
        if (paused ? r.paused_reason !== null : r.paused_reason !== "board_paused") continue;
        r.paused_reason = paused ? "board_paused" : null;
        r.paused_at = paused ? at() : null;
        r.version += 1;
        r.updated_at = at();
      }
      return noteJson(noteOf(p, v.board_id));
    },
    "automations.list": ({ principal: p, url }) => {
      if (!p) return notFound();
      const board = url.searchParams.get("board_id");
      const scope = url.searchParams.get("scope");
      const data = rulesOf(p.company.id)
        .filter((r) => canSee(p, r))
        .filter((r) => !board || r.board_id === board)
        .filter((r) => !scope || r.scope === scope)
        .map((r) => ({ ...r, can_edit: writes(p, r.scope, r.board_id) }))
        .sort((a, b) => a.position - b.position || a.id.localeCompare(b.id));
      const props = (proposals.get(p.company.id) ?? []).filter(
        (x) => !board || x.board_id === board,
      );
      return json({ data, proposals: clientViewerOnly(p) ? [] : props });
    },
    "automations.create": ({ principal: p, body }) => {
      if (!p) return notFound();
      const { enabled, ...s } = (body ?? {}) as Spec;
      const bad = checkSpec(p, s);
      if (bad) return bad;
      const co = p.company.id;
      const list = rulesOf(co);
      const r = view(co, {
        company_id: co,
        id: id(0xa501),
        scope: s.scope,
        board_id: s.board_id,
        name: s.name,
        trigger: s.trigger,
        conditions: s.conditions ?? [],
        actions: s.actions,
        enabled: enabled ?? true,
        owner_membership_id: membershipOf(p) ?? co,
        source: "person",
        approval_id: null,
        proposed_by_seat_id: null,
        effect_budget_per_hour: s.effect_budget_per_hour ?? 200,
        paused_reason: null,
        paused_at: null,
        position: nextPosition(list),
        version: 1,
        fired_count: 0,
        last_fired_at: null,
        actor_kind: "person",
        actor_membership_id: membershipOf(p),
        created_at: at(),
        updated_at: at(),
      });
      list.push(r);
      return json(result(r), 201);
    },
    "automations.draft": ({ principal: p, body }) => {
      if (!p) return notFound();
      const b = (body ?? {}) as { text?: string; board_id?: string };
      return json(draft(p.company.id, b.text ?? "", b.board_id ?? null));
    },
    "automations.recipes": ({ principal: p, url }) => {
      if (!p) return notFound();
      const board = boardOf(p.company.id, url.searchParams.get("board_id"));
      const next = board?.status_set[1] ?? board?.status_set[0];
      return json({
        data: board
          ? [
              ...(next
                ? [
                    {
                      id: "status_entered_tell",
                      text: `When a task moves to “${next.name}”, comment “Have a look”`,
                    },
                  ]
                : []),
              { id: "file_added_tell", text: "When a file is added, comment “A new file is in”" },
              { id: "linked_tell", text: "When a task is linked, comment “Check the link”" },
            ]
          : [],
      });
    },
    "automations.get": ({ principal: p, params }) => {
      if (!p) return notFound();
      const r = ruleIn(p, params.id);
      return r ? json({ ...r, can_edit: writes(p, r.scope, r.board_id) }) : notFound();
    },
    "automations.update": ({ principal: p, params, body, headers }) => {
      if (!p) return notFound();
      const r = ruleIn(p, params.id);
      if (!r) return notFound();
      if (!writes(p, r.scope, r.board_id)) return forbidden("you do not edit this rule");
      const m = /"?(\d+)"?/.exec(headers.get("if-match") ?? "");
      if (!m) return problem(428, "precondition_required", "Precondition required");
      if (Number(m[1]) !== r.version) return conflict(`version is ${r.version}`);
      const b = (body ?? {}) as Partial<Spec> & { position?: number };
      const bad = checkSpec(p, {
        name: b.name ?? r.name,
        scope: r.scope,
        board_id: r.board_id,
        trigger: b.trigger ?? r.trigger,
        conditions: b.conditions ?? r.conditions,
        actions: b.actions ?? r.actions,
      });
      if (bad) return bad;
      return json(result(patch(p, r, b)));
    },
    "automations.delete": ({ principal: p, params }) => {
      if (!p) return notFound();
      const r = ruleIn(p, params.id);
      if (!r) return notFound();
      if (!writes(p, r.scope, r.board_id)) return forbidden("you do not edit this rule");
      const list = rulesOf(p.company.id);
      list.splice(list.indexOf(r), 1);
      return json(result(null));
    },
    "automations.enable": ({ principal: p, params }) => toggle(p, params.id, true),
    "automations.disable": ({ principal: p, params }) => toggle(p, params.id, false),
    "automations.duplicate": ({ principal: p, params }) => {
      if (!p) return notFound();
      const r = ruleIn(p, params.id);
      if (!r) return notFound();
      if (!writes(p, r.scope, r.board_id)) return forbidden("you do not edit this rule");
      const list = rulesOf(p.company.id);
      const copy = view(p.company.id, {
        ...r,
        id: id(0xa501),
        name: `Copy of ${r.name}`.slice(0, 200),
        enabled: false,
        paused_reason: null,
        paused_at: null,
        owner_membership_id: membershipOf(p) ?? r.owner_membership_id,
        source: "person",
        approval_id: null,
        proposed_by_seat_id: null,
        proposed_by_name: null,
        position: nextPosition(list),
        version: 1,
        fired_count: 0,
        last_fired_at: null,
        created_at: at(),
        updated_at: at(),
      });
      list.push(copy);
      return json(result(copy), 201);
    },
    "automations.runs": ({ principal: p, params, url }) => {
      if (!p) return notFound();
      const r = ruleIn(p, params.id);
      if (!r) return notFound();
      const limit = Number(url.searchParams.get("limit") ?? 50) || 50;
      return json({ data: (runs.get(r.id) ?? []).slice(0, limit) });
    },

    "seats.routines": ({ principal: p, params }) => {
      if (!p) return notFound();
      if (!d.seatsOf(p.company.id).some((s) => s.id === params.id)) return notFound();
      return json({
        data: routinesOf(p.company.id).filter((r) => r.responsibility.seat_id === params.id),
      });
    },
    "seats.routinesCreate": ({ principal: p, params, body }) => {
      const no = manager(p);
      if (no || !p) return no ?? notFound();
      const b = (body ?? {}) as Parameters<typeof create>[2] & { board_id?: string };
      if (!b.task_template?.title) return validation("task_template.title: required");
      const out = create(p, params.id ?? "", b, b.board_id ?? null, b.task_template);
      if (out instanceof Response) return out;
      return json({ routine: out, proposed: false, approval_id: null }, 201);
    },
    "tasks.repeat": ({ principal: p, params, body }) => {
      const no = manager(p);
      if (no || !p) return no ?? notFound();
      const t = d.taskOf(p.company.id, params.id ?? "");
      if (!t) return notFound();
      const b = (body ?? {}) as Parameters<typeof create>[2] & { seat_id?: string };
      // the template is the task, as the API copies it: title, priority, assignees
      const template: TaskTemplate = {
        title: t.title,
        ...(t.priority ? { priority: t.priority } : {}),
        ...(t.assignee_seat_ids.length
          ? { assignee_seat_ids: t.assignee_seat_ids.slice(0, 20) }
          : {}),
      };
      const out = create(
        p,
        b.seat_id ?? "",
        b,
        t.board_id,
        template,
        b.after_completion_days ? t.id : null,
      );
      if (out instanceof Response) return out;
      return json(viewOf(p.company.id, out.recurring_work), 201);
    },
    // "Run now": the occurrence at next_run_at becomes a task now (the work twin's), the control
    // keeps it as the last one, and "after completion" parks the series until it is done
    "recurringWork.materialiseNow": ({ principal: p, params }) => {
      const no = manager(p);
      if (no || !p) return no ?? notFound();
      const r = routineIn(p, params.id);
      if (!r) return notFound();
      const w = r.recurring_work;
      if (w.status !== "active" || !w.next_run_at)
        return conflict("The schedule is paused or has ended.");
      const due = new Date(w.next_run_at);
      const now = d.now();
      // the whole template, as the API's materialiser: assignees default to the routine's seat
      const tpl = w.task_template;
      const made = w.board_id
        ? d.createTask(p, {
            board_id: w.board_id,
            title: tpl.title,
            ...(tpl.priority ? { priority: tpl.priority } : {}),
            assignee_seat_ids: tpl.assignee_seat_ids?.length
              ? tpl.assignee_seat_ids
              : [r.responsibility.seat_id],
            ...(tpl.checklist?.length ? { checklist: tpl.checklist } : {}),
            ...(tpl.due_in_days !== undefined
              ? { due_at: new Date(now.getTime() + tpl.due_in_days * DAY).toISOString() }
              : {}),
          })
        : null;
      if (made) setControl(r, { last_task_id: made });
      const after = r.control?.after_completion_days;
      w.last_materialised_at = now.toISOString();
      w.next_run_at =
        after && made ? null : (nextOf(w, due > now ? due : now)?.toISOString() ?? null);
      w.updated_at = now.toISOString();
      record(p, r, "materialised", {
        scheduled_for: due.toISOString(),
        task_id: made,
        trigger: "manual",
        actor_kind: "system",
        actor_membership_id: null,
      });
      return json(w);
    },
    "recurringWork.pause": ({ principal: p, params }) => {
      const no = manager(p);
      if (no || !p) return no ?? notFound();
      const r = routineIn(p, params.id);
      if (!r) return notFound();
      if (r.recurring_work.status === "paused") return conflict("already paused");
      r.recurring_work.status = "paused";
      r.recurring_work.next_run_at = null;
      setControl(r, { paused_at: at() });
      record(p, r, "paused");
      return answer(r);
    },
    "recurringWork.resume": ({ principal: p, params }) => {
      const no = manager(p);
      if (no || !p) return no ?? notFound();
      const r = routineIn(p, params.id);
      if (!r) return notFound();
      if (r.recurring_work.status === "active") return conflict("already running");
      r.recurring_work.status = "active";
      // from the next occurrence after now; "after completion" waits for an open last task
      const next = r.control?.after_completion_days
        ? afterCompletionNext(r.control)
        : nextOf(r.recurring_work, d.now());
      r.recurring_work.next_run_at = next?.toISOString() ?? null;
      setControl(r, { paused_at: null });
      record(p, r, "resumed");
      return answer(r);
    },
    "recurringWork.skip": ({ principal: p, params }) => {
      const no = manager(p);
      if (no || !p) return no ?? notFound();
      const r = routineIn(p, params.id);
      if (!r) return notFound();
      if (r.recurring_work.status !== "active") return conflict("the routine is paused");
      if (!r.recurring_work.next_run_at) return conflict("no occurrence is scheduled");
      const skipped = r.recurring_work.next_run_at;
      // as the API: "after completion" moves N days on; a cadence to its next occurrence
      const days = r.control?.after_completion_days;
      r.recurring_work.next_run_at = days
        ? new Date(Date.parse(skipped) + days * DAY).toISOString()
        : (nextOf(r.recurring_work, new Date(skipped))?.toISOString() ?? null);
      record(p, r, "skipped", { scheduled_for: skipped });
      return answer(r);
    },
    "recurringWork.takeOver": ({ principal: p, params, body }) => {
      const no = manager(p);
      if (no || !p) return no ?? notFound();
      const r = routineIn(p, params.id);
      if (!r) return notFound();
      const b = (body ?? {}) as { scope?: string; seat_id?: string };
      const seat = d.seatsOf(p.company.id).find((s) => s.id === b.seat_id);
      if (!seat) return validation("seat_id: name the seat taking the work over");
      if (seat.status === "closed") return problem(409, "seat_closed", "Seat is closed");
      if (b.scope === "occurrence") {
        // the open occurrence (the control's last task, not done): reassigned to the taker
        const last = r.control?.last_task_id;
        const t = last ? d.taskOf(p.company.id, last) : null;
        if (!t || t.completed_at)
          return conflict("no occurrence is open: take over all future occurrences instead");
        const own = r.responsibility.seat_id;
        const seats = t.assignee_seat_ids.filter((s) => s !== own || s === seat.id);
        if (!seats.includes(seat.id)) seats.push(seat.id);
        t.assignee_seat_ids.splice(0, t.assignee_seat_ids.length, ...seats);
        record(p, r, "taken_over", { seat_id: seat.id, task_id: t.id, trigger: "occurrence" });
        return answer(r);
      }
      const back = seat.id === r.responsibility.seat_id;
      const { assignee_seat_ids: _drop, ...rest } = r.recurring_work.task_template;
      r.recurring_work.task_template = back ? rest : { ...rest, assignee_seat_ids: [seat.id] };
      setControl(r, {
        taken_over_by_seat_id: back ? null : seat.id,
        takeover_scope: back ? null : "all",
      });
      record(p, r, "taken_over", { seat_id: seat.id, trigger: "all" });
      return answer(r);
    },
    "recurringWork.history": ({ principal: p, params }) => {
      if (!p) return notFound();
      const r = routineIn(p, params.id);
      return r ? json({ data: history.get(r.recurring_work.id) ?? [] }) : notFound();
    },
  };

  function toggle(p: Principal | null, rid: string | undefined, enabled: boolean): Response {
    if (!p) return notFound();
    const r = ruleIn(p, rid);
    if (!r) return notFound();
    if (!writes(p, r.scope, r.board_id)) return forbidden("you do not edit this rule");
    return json(result(patch(p, r, { enabled })));
  }

  /** "Describe it": a small deterministic reading of the sentence (the API's grammar, abridged). */
  function draft(co: string, text: string, boardId: string | null) {
    const statuses = boardOf(co, boardId)?.status_set ?? [];
    const out: OperationOutput<"automations.draft"> = {
      name: text.trim().slice(0, 80),
      trigger: null,
      conditions: [],
      actions: [],
      understood: [],
      unmatched: [],
    };
    const find = (raw: string) => {
      const n = raw
        .trim()
        .toLowerCase()
        .replace(/^the\s+/, "")
        .replace(/[.”"]+$/, "");
      return statuses.find((s) => s.name.toLowerCase() === n || s.key === n);
    };
    for (const clause of text
      .split(/,|;|\.|\bthen\b|\band\b/i)
      .map((c) => c.trim())
      .filter(Boolean)) {
      const s = clause.toLowerCase();
      const to = /(?:moves? to|becomes|status changes to)\s+(.+)$/.exec(s);
      if (!out.trigger && /^(when|whenever)/.test(s)) {
        const st = to ? find(to[1] ?? "") : undefined;
        const t: Trigger | null = st
          ? { type: "task.status_changed", to: st.key }
          : /created|added/.test(s)
            ? { type: "task.created" }
            : /completed|done|finished/.test(s)
              ? { type: "task.completed" }
              : /overdue/.test(s)
                ? { type: "task.overdue" }
                : /comment/.test(s)
                  ? { type: "task.commented" }
                  : null;
        if (t) {
          out.trigger = t;
          out.understood.push(clause);
        } else out.unmatched.push(clause);
        continue;
      }
      const pr = /priority to\s+(\w+)/.exec(s);
      const quoted = /["“](.+?)["”]/.exec(clause)?.[1];
      const ss = /^(?:set status to|mark (?:it )?(?:as )?|move (?:it )?to)\s+(.+)$/.exec(s);
      let x: Action | null = null;
      if (pr && PRIORITY.indexOf(pr[1] ?? "") >= 0)
        x = { type: "set_priority", priority: PRIORITY.indexOf(pr[1] ?? "") };
      else if (/^comment/.test(s) && quoted) x = { type: "add_comment", text: quoted };
      else if (ss) {
        const st = find(ss[1] ?? "");
        if (st) x = { type: "set_status", status: st.key };
      }
      if (x) {
        out.actions.push(x);
        out.understood.push(clause);
      } else out.unmatched.push(clause);
    }
    return out;
  }
}
